import AddIcon from '@mui/icons-material/Add'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import {
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  OutlinedInput,
  Pagination,
  PaginationItem,
  Stack,
} from '@mui/material'
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  useLazyGetBranchesQuery,
  useLazyGetEmployeesQuery,
  useLazyGetExelEmployeeQuery,
} from '@/api'
import type { Employee } from '@/api/models/company'
import { type Item, ViewSettings } from '@/components'
import { useRole } from '@/hooks'
import { useCompany } from '@/hooks/useCompany'
import { SearchIcon } from '@/modules/MedComplexList/icons'
import { DownloadIcon } from '@/modules/MedReports/Reports/assets/DownloadIcon'
import { OrganizationContext } from '@/modules/Organization/Organization'
import { appStore, useAppDispatch, useAppSelector } from '@/store'
import { setCurrentHasBranches } from '@/store/app'
import { downloadFromBlobUrl } from '@/utils/helpers'
import { showAddNotice } from './components/AddNotice/showAddNotice'
import { ImportEmployeesDrawer } from './components/drawers'
import { EmployeesTable } from './components/EmployeesTable/EmployeesTable'
import { EmployeeStatus } from '../../components'
import { EmployeeRoles } from '../../components/EmployeeRoles'

export const EmployeesList = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { organizationId, user } = useContext(OrganizationContext)
  const { currentHasBranches } = useAppSelector(appStore)

  const [getBranches] = useLazyGetBranchesQuery()
  const [getEmployees, { data: employees }] = useLazyGetEmployeesQuery()
  const { getAccess, hasRole } = useRole()
  const isClientAdmin = hasRole(['Администратор клиента'])
  const isAddEmployees =
    hasRole([
      'Руководитель (Менеджер)',
      'Руководитель (НТЦ)',
      'Старший медик',
    ]) || isClientAdmin

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [filterQuery, setFilterQuery] = useState<any>()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [sortQuery, setSortQuery] = useState<any>({ id: 'asc' })

  const [openViewSettings, setOpenViewSettings] = useState<boolean>(false)

  const [cards, setCards] = useState<Item[]>([
    {
      id: 1,
      text: 'Статус',
      // data: (item: Employee) => <EmployeeStatus status={item?.status} />,
      data: (item: Employee) => <EmployeeStatus status={'CONFIRMED'} />,
      visible: true,
      // sort: (sort: 'asc' | 'desc' | '') => {
      //   setSortQuery({ model: { id: sort } })
      //   setSortMode(1, sort)
      // },
      // // eslint-disable-next-line @typescript-eslint/no-explicit-any
      // filter: (handleClose: any, setMode: any) => (
      //   <TypeFilter
      //     setFilterRequest={setFilterQuery}
      //     handleClose={handleClose}
      //     setMode={setMode}
      //   />
      // ),
    },
    {
      id: 2,
      text: 'Ф.И.О',
      data: (item: Employee) =>
        item.lastname + ' ' + item.firstname + ' ' + (item?.middlename ?? ''),
      visible: true,
    },
    {
      id: 3,
      text: 'Табельный номер',
      data: (item: Employee) => item?.service_number,
      visible: true,
    },
    {
      id: 4,
      text: 'Должность',
      data: (item: Employee) => item?.position?.name,
      visible: true,
    },
    {
      id: 5,
      text: 'Подразделение',
      data: (item: Employee) =>
        item?.branches ? item?.branches[0]?.branch?.name : null,
      visible: true,
    },
    {
      id: 6,
      text: 'Роли',
      data: (item: Employee) => (
        <EmployeeRoles
          roles={[
            ...(item?.roles ?? []),
            ...(!item?.roles?.includes('Администратор Тетрон') &&
            item?.branchAdmin
              ? [`Руководитель ${'подразделения'}`]
              : []),
          ]}
        />
      ),
      visible: true,
    },
  ])

  const setSortMode = (id: number, mode: 'asc' | 'desc' | '') => {
    setCards((prev) =>
      prev.map((item: Item) =>
        item.id === id ? { ...item, sortMode: mode } : { ...item, sortMode: '' }
      )
    )
  }

  const [page, setPage] = useState<number>(1)
  const [pageQty, setPageQty] = useState(0)
  const [perPage, setPerPage] = useState<number>(50)

  const { activeCompany } = useCompany()

  useMemo(async () => {
    if (page && perPage) {
      let response
      if (currentHasBranches === null) {
        response = await getBranches({
          companyId: Number(organizationId),
          params: {
            parentIds: 0,
          },
        })
        dispatch(setCurrentHasBranches(Boolean(response?.data?.length)))
      }
      await getEmployees({
        id: organizationId,
        params: {
          page: page,
          per_page: perPage,
          ...filterQuery,
          ...(hasRole('Администратор Тетрон') ||
          !(currentHasBranches ?? Boolean(response?.data?.length)) ||
          (hasRole('Администратор клиента') &&
            (user?.branches ?? []).length === 0)
            ? {}
            : {
                branchIds: user?.branches,
              }),
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }).then((response: any) => {
        setPageQty(response?.data?.last_page)
      })
    }
  }, [page, perPage, sortQuery, filterQuery, organizationId])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const changePage = (num: number) => {
    setPage(num)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [typingTimer, setTypingTimer] = useState<any>()
  const typingListener = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    inputValue: any,
    typingPause: number,
    callback: () => void
  ) => {
    clearTimeout(typingTimer)
    setTypingTimer(setTimeout(callback, typingPause))
  }

  const searchRequest = (event: React.ChangeEvent<HTMLInputElement>) => {
    typingListener(event.target.value, 800, () => {
      if (event.target.value !== '') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setFilterQuery((prev: any) => ({
          ...prev,
          search: event.target.value,
        }))
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setFilterQuery((prev: any) => delete prev.search)
      }
    })
  }

  const [exelLoading, setExelLoading] = useState<boolean>(false)

  const [getExelEmployee] = useLazyGetExelEmployeeQuery()

  const getExcelEmployeeTable = async () => {
    try {
      setExelLoading(true)
      const response = await getExelEmployee(organizationId)
      setExelLoading(false)
      downloadFromBlobUrl(response.data, 'table.xlsx')
    } catch (e) {
      console.log(e)
    }
    return {
      //
    }
  }

  const [openImportDrawer, setOpenImportDrawer] = useState<boolean>(false)

  return (
    <Stack gap={'16px'}>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <FormControl
          sx={{
            '& .MuiInputBase-input': {
              WebkitBoxShadow: '0 0 0 1000px white inset',
            },
            '& .MuiOutlinedInput-root': {
              maxWidth: '307px',
              minWidth: '307px',
              maxHeight: '36px',
              minHeight: '36px',
              paddingRight: '6px',
              '& input::placeholder': {
                color: '#8E8E8E',
                opacity: 1,
              },
              '& fieldset': {
                border: '2px solid #D9D9D9',
              },
              '& .MuiInputAdornment-root': {
                pointerEvents: 'none',
              },
              '&.MuiOutlinedInput-root:hover': {
                opacity: 1,
                '& fieldset': {
                  border: '2px solid #000000',
                },
              },
              '&.MuiOutlinedInput-root.Mui-focused': {
                opacity: 1,
                '& fieldset': {
                  border: '2px solid #009FDA',
                },
              },
            },
          }}
        >
          <OutlinedInput
            placeholder="Поиск"
            endAdornment={
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            }
            inputProps={{ form: { autoComplete: 'off' } }}
            autoComplete="off"
            onChange={searchRequest}
          />
        </FormControl>
        <Stack direction={'row'} gap={'12px'} ml={'auto'}>
          {(getAccess('ORGANIZATION', ['UPDATE', 'ADMIN']) ||
            isClientAdmin) && (
            <Button
              onClick={async () => {
                if (
                  isClientAdmin &&
                  !(await showAddNotice({
                    contentText: (
                      <>
                        При массовой загрузке из Excel будут добавлены только
                        сотрудники с ролью «Мед. осматриваемый».
                        <br />
                        Строки с другой ролью или незаполненной ролью будут
                        проигнорированы.
                      </>
                    ),
                  }))
                ) {
                  return
                }

                setOpenImportDrawer(true)
              }}
              variant={'contained'}
              color={'primary'}
            >
              Импортировать
            </Button>
          )}
          <Button
            onClick={() => getExcelEmployeeTable()}
            variant={'contained'}
            color={'primary'}
            startIcon={
              exelLoading ? (
                <CircularProgress sx={{ color: '#FFFFFF' }} size={'20px'} />
              ) : (
                <DownloadIcon />
              )
            }
          >
            Скачать список
          </Button>

          {getAccess('ORGANIZATION', ['UPDATE', 'ADMIN']) || isAddEmployees ? (
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={async () => {
                if (
                  isClientAdmin &&
                  !(await showAddNotice({
                    contentText: (
                      <>
                        Вы можете добавлять только сотрудников с ролью «Мед.
                        осматриваемый».
                        <br />
                        Другие роли недоступны для назначения.
                      </>
                    ),
                  }))
                ) {
                  return
                }

                navigate(
                  `/organization/${organizationId}/employee/create/general`
                )
              }}
            >
              Добавить
            </Button>
          ) : null}

          {/* <Button
                variant="contained"
                //onClick={}
              >
                <TrashIcon />
              </Button> */}
          <Button variant="contained" onClick={() => setOpenViewSettings(true)}>
            <SettingsOutlinedIcon />
          </Button>
        </Stack>
      </Stack>
      {/* {companies && ( */}
      <EmployeesTable data={employees?.data || []} cards={cards} />
      {/* )} */}
      <ViewSettings
        open={openViewSettings}
        handleClose={() => setOpenViewSettings(false)}
        cards={cards}
        setCards={setCards}
      />
      <Stack
        direction={'row'}
        justifyContent={'center'}
        sx={{
          backgroundColor: '#FAFAFA',
          position: 'sticky',
          bottom: '0px',
          padding: '8px',
        }}
      >
        <Pagination
          count={pageQty}
          page={page}
          onChange={(_, num) => changePage(num)}
          variant="outlined"
          shape="rounded"
          sx={{
            //marginY: 3, marginX: "auto",
            '& .Mui-selected': {
              backgroundColor: '#009FDA !important',
              color: 'white !important',
            },
            '& .Mui-selected:hover': {
              backgroundColor: 'rgb(0,159,218) !important',
              color: 'white !important',
            },
          }}
          renderItem={(item) => (
            <PaginationItem
              {...item}
              component={NavLink}
              //to={`/medcomplex/${item.page}`}
              to={`/organization/${organizationId}/employee`}
            />
          )}
        />
      </Stack>
      <ImportEmployeesDrawer
        open={openImportDrawer}
        setOpen={setOpenImportDrawer}
      />
    </Stack>
  )
}
